import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-white/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "/"} className="text-sm font-medium text-[#141414]/70 hover:text-[#141414] transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full bg-[#141414]/5 animate-pulse rounded-lg" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block py-4 text-lg font-medium text-[#141414] hover:text-[#FF6F61] transition-colors" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className="sticky top-0 z-50 w-full px-3 sm:px-4 pt-3">
    <div className={cn("mx-auto max-w-7xl rounded-[1rem] transition-all duration-300 border", isScrolled ? "bg-white/70 backdrop-blur-xl border-white/50 shadow-lg" : "bg-white/40 backdrop-blur-sm border-white/30")}>
      <div className="px-6 h-16 flex items-center justify-between">
        {/* Left: Navigation Links */}
        <div className="hidden lg:flex items-center">
          <MenuProvider menu_id="43" className="hidden lg:block">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {/* Mobile: Menu Button (left) */}
        <div className="lg:hidden">
          <Button variant="ghost" size="icon" className="text-[#141414]" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Center: Logo */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <Link to="/" className="block">
            <img src="https://perfect-duck.10web.cloud/wp-content/uploads/2026/03/Coworking-Kassel-Logo.png" alt="BLUVIT GmbH - Coworking Space Kassel" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {/* Right: Contact Button (desktop only) */}
        <div className="hidden lg:flex items-center">
          <Button asChild className="rounded-full bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all px-6 h-10">
            <Link to="/kontakt">Kontakt</Link>
          </Button>
        </div>

        {/* Mobile: empty spacer for centering logo */}
        <div className="lg:hidden w-10" />
      </div>
    </div>

    {/* Mobile Menu Overlay */}
    {isMobileMenuOpen && <>
      {/* Backdrop */}
      <div className="lg:hidden fixed inset-0 top-0 bg-black/20 backdrop-blur-sm z-40" onClick={() => setIsMobileMenuOpen(false)} />
      {/* Menu */}
      <div className="lg:hidden fixed top-[76px] left-3 right-3 sm:left-4 sm:right-4 z-50 rounded-[1rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl overflow-hidden">
        <div className="py-2">
          <MenuProvider menu_id="43">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <Button asChild className="w-full justify-center font-semibold rounded-full bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white h-12">
              <Link to="/kontakt" onClick={() => setIsMobileMenuOpen(false)}>
                Kontakt aufnehmen
              </Link>
            </Button>
            <Button asChild variant="ghost" className="w-full justify-center font-medium rounded-full text-[#141414] hover:bg-[#141414]/5 h-12">
              <Link to="tel:+4956151090993">
                <Phone className="mr-2 h-4 w-4" />
                0561 51090 993
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>}
  </header>;
}